import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useBreadcrumbTitle } from '@/lib/breadcrumbStore';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { RequestMaterialDialog } from '@/components/materials/RequestMaterialDialog';
import { SimilarMaterialsSection } from '@/components/materials/SimilarMaterialsSection';
import { MatchingWantedAdsSection } from '@/components/materials/MatchingWantedAdsSection';
import { getMaterial, getSimilarMaterials, getSKU, addToWatchlist, removeFromWatchlist, getWatchlist, matchMasterData, deleteMaterial, releaseSkuExternal, getSavingForMaterial, updateSkuInternalWindow } from '@/lib/api';
import { setPageSeo, setJsonLd } from '@/lib/seo';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { MaterialFileUpload, AttachmentsList } from '@/components/material/FileUpload';
import { SpecSheetPreview, pickSpecSheet } from '@/components/material/SpecSheetPreview';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Heart, 
  MapPin, 
  Package, 
  Ruler, 
  Calendar, 
  FileText, 
  Download, 
  ChevronLeft,
  Building,
  Phone,
  Mail,
  Shield,
  AlertTriangle,
  Edit,
  Trash2,
  Lock,
  Star,
  Globe,
  TrendingDown,
  Leaf,
  UtensilsCrossed,
  ClipboardList,
  Timer,
  ShieldCheck
} from 'lucide-react';
import { cn, fmt2, fmtEUR, pricePerUnit, calcLaufmeter, isSqmUnit } from '@/lib/utils';
import { LazyImage } from '@/components/ui/LazyImage';

const PLACEHOLDER_IMAGE = 'https://customer-assets.emergentagent.com/job_ee642069-4fc1-425d-bfbc-e67ee20a2646/artifacts/vxtmarer_ChatGPT%20Image%202.%20M%C3%A4rz%202026%2C%2012_35_03.png';

const AGE_BUCKET_LABELS = {
  '0_90': '0-90 Tage',
  '91_180': '91-180 Tage',
  '181_360': '181-360 Tage',
  'gt_360': '>360 Tage'
};

export const MaterialDetailPage = () => {
  const { t } = useTranslation();
  const { materialId } = useParams();
  const [searchParams] = useSearchParams();
  const skuId = searchParams.get('sku');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isApproved } = useAuth();
  
  const [material, setMaterial] = useState(null);
  useBreadcrumbTitle(material?.common_title || material?.article_number);
  const [selectedSku, setSelectedSku] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isInWatchlist, setIsInWatchlist] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [masterDataSheet, setMasterDataSheet] = useState(null);
  const [masterDataMatch, setMasterDataMatch] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [savingInfo, setSavingInfo] = useState(null);
  const [similarMaterials, setSimilarMaterials] = useState([]);

  useEffect(() => {
    loadMaterial();
    if (user) checkWatchlist();
  }, [materialId, skuId]);

  // SEO: keep title + og:* tags in sync once material is loaded, plus inject
  // a Product JSON-LD block so search engines can render rich snippets (price,
  // availability, brand, rating) directly in results.
  useEffect(() => {
    if (!material) return;
    const title = `${material.common_title || 'Material'} | Surplify`;
    const parts = [];
    if (material.manufacturer_name) parts.push(material.manufacturer_name);
    if (material.material_width_mm) parts.push(`${material.material_width_mm}mm`);
    if (material.common_description) parts.push(material.common_description);
    const description = parts.join(' • ').slice(0, 180) ||
      'Surplusmaterial auf dem B2B-Verpackungsmarktplatz Surplify.';
    const photo = (material.attachments || []).find((a) => a.type === 'photo');
    const image = photo?.file_url || material.category?.default_image_url;
    const url = typeof window !== 'undefined' ? window.location.href : undefined;
    const seoCleanup = setPageSeo({ title, description, image, url, type: 'product' });

    // Build Product JSON-LD. Schema.org "Product" with nested "Offer" and
    // "AggregateRating" so Google can show price + stars in SERP snippets.
    const firstSku = material.skus?.[0];
    const listPriceEUR = firstSku?.list_price_kEUR != null ? firstSku.list_price_kEUR * 1000 : null;
    const availability = firstSku?.status === 'available'
      ? 'https://schema.org/InStock'
      : firstSku?.status === 'reserved'
        ? 'https://schema.org/LimitedAvailability'
        : 'https://schema.org/PreOrder';
    const rating = material.group_rating;
    const jsonLd = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: material.common_title,
      description,
      ...(image ? { image: [image] } : {}),
      ...(material.manufacturer_part_number ? { sku: material.manufacturer_part_number, mpn: material.manufacturer_part_number } : {}),
      ...(material.manufacturer_name ? { brand: { '@type': 'Brand', name: material.manufacturer_name } } : {}),
      ...(url ? { url } : {}),
      ...(listPriceEUR != null ? {
        offers: {
          '@type': 'Offer',
          priceCurrency: 'EUR',
          price: Number(listPriceEUR).toFixed(2),
          availability,
          ...(url ? { url } : {}),
        },
      } : {}),
      ...(rating?.average && rating?.total_ratings ? {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: rating.average,
          reviewCount: rating.total_ratings,
          bestRating: 5,
          worstRating: 1,
        },
      } : {}),
    };
    const ldCleanup = setJsonLd('material-product', jsonLd);
    return () => { seoCleanup(); ldCleanup(); };
  }, [material]);

  const loadMaterial = async () => {
    setLoading(true);
    try {
      const response = await getMaterial(materialId);
      setMaterial(response.data);
      
      // Select the specified SKU or first available
      if (skuId) {
        const sku = response.data.skus.find(s => s.sku_id === skuId);
        setSelectedSku(sku || response.data.skus[0]);
      } else if (response.data.skus.length > 0) {
        setSelectedSku(response.data.skus[0]);
      }

      // Fetch master data match to get data sheet and full info
      const mat = response.data;
      if (mat.manufacturer_name || mat.manufacturer_part_number) {
        try {
          const matchRes = await matchMasterData(mat.manufacturer_name || '', mat.manufacturer_part_number || '');
          const matches = matchRes.data.matches || [];
          const exactMatch = matches.find(m =>
            m.supplier_name?.toLowerCase() === (mat.manufacturer_name || '').toLowerCase() &&
            m.supplier_article_number?.toLowerCase() === (mat.manufacturer_part_number || '').toLowerCase()
          );
          const bestMatch = exactMatch || matches[0];
          if (bestMatch) {
            setMasterDataMatch(bestMatch);
          }
          if (exactMatch?.data_sheet_url) {
            setMasterDataSheet({ url: exactMatch.data_sheet_url, filename: exactMatch.data_sheet_filename || 'Datenblatt' });
          } else {
            const withSheet = matches.find(m => m.data_sheet_url);
            if (withSheet?.data_sheet_url) {
              setMasterDataSheet({ url: withSheet.data_sheet_url, filename: withSheet.data_sheet_filename || 'Datenblatt' });
            }
          }
        } catch { /* ignore match errors */ }
      }
      
      // Check for savings from standard material list
      if (user) {
        try {
          const savingRes = await getSavingForMaterial(materialId);
          if (savingRes.data?.match) {
            setSavingInfo(savingRes.data);
          }
        } catch { /* ignore */ }
      }

      // Fetch similar materials (public endpoint — safe for anonymous users)
      try {
        const simRes = await getSimilarMaterials(materialId, 4);
        setSimilarMaterials(simRes.data || []);
      } catch { /* ignore */ }
    } catch (error) {
      console.error('Failed to load material:', error);
      toast.error('Failed to load material details');
    } finally {
      setLoading(false);
    }
  };

  const checkWatchlist = async () => {
    try {
      const response = await getWatchlist();
      setIsInWatchlist(response.data.some(item => item.material_id === materialId));
    } catch (error) {
      console.error('Failed to check watchlist:', error);
    }
  };

  const handleWatchlistToggle = async () => {
    if (!user) {
      toast.error('Please login to use watchlist');
      return;
    }

    try {
      if (isInWatchlist) {
        await removeFromWatchlist(materialId);
        setIsInWatchlist(false);
        toast.success(t('material.removeFromWatchlist'));
      } else {
        await addToWatchlist(materialId);
        setIsInWatchlist(true);
        toast.success(t('material.addToWatchlist'));
      }
    } catch (error) {
      console.error('Watchlist error:', error);
      toast.error('Failed to update watchlist');
    }
  };

  const photos = material?.attachments?.filter(a => a.type === 'photo') || [];
  const documents = material?.attachments?.filter(a => a.type !== 'photo') || [];
  const catDefault = material?.category;
  const displayImages = photos.length > 0 
    ? photos.map(p => ({ full: p.file_url, thumb: p.thumbnail_url || p.file_url, blur: p.blur_hash })) 
    : catDefault?.default_image_url
      ? [{ full: catDefault.default_image_url, thumb: catDefault.default_thumbnail_url || catDefault.default_image_url, blur: catDefault.default_blur_hash }]
      : [{ full: PLACEHOLDER_IMAGE, thumb: PLACEHOLDER_IMAGE, blur: null }];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <Skeleton className="aspect-[4/3] w-full rounded-lg" />
            </div>
            <div className="lg:col-span-5 space-y-4">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!material) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold mb-4">Material Not Found</h1>
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </div>
      </div>
    );
  }

  const isInternal = selectedSku?.internal_only_until_date && 
    new Date(selectedSku.internal_only_until_date) > new Date();
  const hasMissingDocs = selectedSku?.status === 'documentation_missing';
  const isSpecial = material?.is_special;
  const accessTier = material?.access_tier || (user ? (isApproved ? 3 : 2) : 1);
  const isPublic = accessTier === 1;            // anonymous viewer
  const restricted = accessTier < 3;            // not yet approved
  const publicPriceEUR = selectedSku?.list_price_kEUR != null
    ? selectedSku.list_price_kEUR * 1000
    : null;
  const internalPriceEUR = selectedSku?.book_value_kEUR != null
    ? selectedSku.book_value_kEUR * 1000
    : null;
  // Price actually shown — internal book-value preferred for same-group users,
  // otherwise the public list price.
  const displayPriceEUR = internalPriceEUR ?? publicPriceEUR;
  const canManage = user && (
    user.role === 'admin' || 
    user.role === 'group_admin' || 
    material?.owner_user_id === user.user_id ||
    (user.group_id && material?.group_id === user.group_id)
  );

  const handleRequestClick = () => {
    // Redirect unauthenticated visitors to login with a return URL so they
    // land back on this material page after sign-in.
    if (!user) {
      const returnTo = `/material/${materialId}${skuId ? `?sku=${skuId}` : ''}`;
      navigate(`/login?next=${encodeURIComponent(returnTo)}`);
      return;
    }
    if (!isApproved) {
      toast.error(t('restricted.approvalHint', 'Ihr Konto wartet auf Freigabe.'));
      return;
    }
    setRequestDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await deleteMaterial(materialId);
      toast.success(t('myMaterials.deleted') || 'Material gelöscht');
      navigate('/my-group');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleReleaseExternal = async () => {
    if (!selectedSku) return;
    try {
      await releaseSkuExternal(selectedSku.sku_id);
      toast.success('SKU wurde für externe Sichtbarkeit freigegeben');
      loadMaterial();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Freigabe');
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="material-detail-page">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-2">
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Tech Spec PDF (primary preview) + photo gallery */}
          <div className="lg:col-span-7 space-y-4">
            <SpecSheetPreview
              specSheet={pickSpecSheet(material.attachments)}
              canManage={canManage}
              onUploadClick={() => navigate(`/material/${materialId}/edit`)}
            />

            {displayImages.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-2" data-testid="material-photo-strip">
                {displayImages.map((img, i) => (
                  <a
                    key={i}
                    href={img.full}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      "w-20 h-20 rounded-lg overflow-hidden shrink-0 border-2 transition-colors block",
                      "border-transparent hover:border-orange-500"
                    )}
                    data-testid={`photo-thumb-${i}`}
                    aria-label={`Foto ${i + 1} in voller Größe öffnen`}
                  >
                    <LazyImage src={img.thumb} blurSrc={img.blur} alt="" className="w-full h-full" fallbackSrc={PLACEHOLDER_IMAGE} />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Details Panel */}
          <div className="lg:col-span-5 space-y-6">
            {/* Title & Badges */}
            <div>
              <div className="flex flex-wrap gap-2 mb-3">
                {isSpecial && (
                  <Badge className="bg-amber-500 text-white gap-1" data-testid="special-material-badge">
                    <Star className="h-3 w-3" />
                    Spezialmaterial
                  </Badge>
                )}
                {isInternal && (
                  <Badge variant="secondary" className="bg-blue-500 text-white">
                    {t('badge.groupOnly')}
                  </Badge>
                )}
                {!isInternal && (
                  <Badge variant="secondary" className="bg-green-500 text-white">
                    {t('badge.external')}
                  </Badge>
                )}
                {hasMissingDocs && (
                  <Badge variant="outline" className="text-orange-600 border-orange-300">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    {t('badge.missingDocs')}
                  </Badge>
                )}
              </div>
              
              <h1 className="font-manrope text-2xl md:text-3xl font-bold tracking-tight mb-2">
                {material.common_title}
              </h1>
              
              <p className="text-muted-foreground flex items-center gap-1.5">
                {material.manufacturer_verified && <ShieldCheck className="h-4 w-4 text-emerald-500" />}
                {material.manufacturer_name} • {material.manufacturer_part_number}
              </p>

              {/* Seller Rating for selected site/werk */}
              {(() => {
                const siteRating = selectedSku?.site_rating;
                const groupRating = material.group_rating;
                const rating = siteRating || groupRating;
                if (!rating) return null;
                const label = siteRating 
                  ? (selectedSku?.site?.site_name || t('transactions.site', 'Werk'))
                  : t('transactions.group', 'Gruppe');
                return (
                  <div className="flex items-center gap-2 mt-1" data-testid="detail-seller-rating">
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star
                          key={s}
                          className={cn(
                            "h-4 w-4",
                            s <= Math.round(rating.average)
                              ? "fill-amber-400 text-amber-400"
                              : "text-gray-300 dark:text-gray-600"
                          )}
                        />
                      ))}
                    </div>
                    <span className="text-sm font-medium text-amber-600">{rating.average}</span>
                    <span className="text-xs text-muted-foreground">
                      ({rating.total_ratings} {rating.total_ratings === 1 ? t('transactions.review', 'Bewertung') : t('transactions.reviews', 'Bewertungen')})
                    </span>
                    <span className="text-xs text-muted-foreground">• {label}</span>
                  </div>
                );
              })()}
            </div>

            {/* Price (public-accessible) */}
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg" data-testid="material-price-block">
              <p className="text-3xl font-bold text-orange-500" data-testid="material-book-value">
                {displayPriceEUR != null
                  ? `\u20AC${fmtEUR(displayPriceEUR / 1000)}`
                  : t('material.priceOnRequest', 'Preis auf Anfrage')}
              </p>
              <p className="text-sm text-muted-foreground mt-1" data-testid="material-quantity-line">
                {isPublic
                  ? (selectedSku?.quantity_range || t('material.quantityOnRequest', 'Menge auf Anfrage'))
                  : (
                    <>
                      {fmt2(selectedSku?.quantity)} {selectedSku?.uom} verfügbar
                      {selectedSku?.number_of_rolls && ` | ${selectedSku.number_of_rolls} Rollen`}
                    </>
                  )}
              </p>

              {/* Seller company + country flag — safe to show publicly */}
              {(material.seller_group_name || selectedSku?.site?.country) && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-sm text-slate-700 dark:text-slate-300" data-testid="material-seller-line">
                  {material.seller_group_name && (
                    <span className="inline-flex items-center gap-1.5">
                      <Building className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{material.seller_group_name}</span>
                    </span>
                  )}
                  {selectedSku?.site?.country && (
                    <span className="inline-flex items-center gap-1.5 text-muted-foreground" data-testid="material-country-flag">
                      <Globe className="h-4 w-4" />
                      {selectedSku.site.city ? `${selectedSku.site.city}, ` : ''}{selectedSku.site.country}
                    </span>
                  )}
                </div>
              )}

              {/* Approved-only: per-unit price + laufmeter + savings */}
              {!restricted && displayPriceEUR != null && selectedSku?.quantity > 0 && (
                <p className="text-sm text-orange-400 mt-2 font-medium" data-testid="material-price-per-unit">
                  {fmt2(pricePerUnit(displayPriceEUR, selectedSku.quantity))} EUR/{selectedSku.uom}
                </p>
              )}
              {!restricted && isSqmUnit(selectedSku?.uom) && material?.material_width_mm > 0 && selectedSku?.quantity > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700" data-testid="laufmeter-section">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Laufmeter: {fmt2(calcLaufmeter(selectedSku.quantity, material.material_width_mm))} lfm
                  </p>
                  {selectedSku?.number_of_rolls > 0 && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {fmt2(calcLaufmeter(selectedSku.quantity, material.material_width_mm) / selectedSku.number_of_rolls)} lfm/Rolle ({selectedSku.number_of_rolls} Rollen)
                    </p>
                  )}
                </div>
              )}
              {!restricted && savingInfo?.match && savingInfo.standard_item && (() => {
                const std = savingInfo.standard_item;
                const hasSaving = savingInfo.ek_price_eur != null && selectedSku?.book_value_kEUR && selectedSku?.quantity > 0;
                let savingPerUnit = null;
                if (hasSaving) {
                  const platformPricePerUnit = (selectedSku.book_value_kEUR * 1000) / selectedSku.quantity;
                  savingPerUnit = savingInfo.ek_price_eur - platformPricePerUnit;
                }
                return (
                  <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden" data-testid="standard-material-info">
                    <div className="bg-slate-100 dark:bg-slate-800 px-4 py-2 flex items-center gap-2">
                      <ClipboardList className="h-4 w-4 text-orange-500" />
                      <span className="text-sm font-semibold">Artikelstammdaten (Match)</span>
                    </div>
                    <div className="px-4 py-3 space-y-2 text-sm">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        <span className="text-muted-foreground">Hersteller:</span>
                        <span className="font-medium">{std.manufacturer_name}</span>
                        <span className="text-muted-foreground">Artikelnummer:</span>
                        <span className="font-medium">{std.manufacturer_part_number}</span>
                        {std.material_description && (
                          <>
                            <span className="text-muted-foreground">Beschreibung:</span>
                            <span>{std.material_description}</span>
                          </>
                        )}
                        {std.ek_price_eur != null && (
                          <>
                            <span className="text-muted-foreground">EK-Preis:</span>
                            <span className="font-medium">{fmt2(std.ek_price_eur)} EUR{std.ek_price_uom ? `/${std.ek_price_uom}` : ''}</span>
                          </>
                        )}
                      </div>
                      {(std.fsc || std.food_safe) && (
                        <div className="flex gap-2 pt-1">
                          {std.fsc && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300" data-testid="std-fsc-badge">
                              <Leaf className="h-3 w-3" /> FSC
                            </span>
                          )}
                          {std.food_safe && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300" data-testid="std-food-safe-badge">
                              <UtensilsCrossed className="h-3 w-3" /> Food Safe
                            </span>
                          )}
                        </div>
                      )}
                      {savingPerUnit != null && savingPerUnit > 0 && (
                        <div className="mt-2 pt-2 border-t border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10 rounded p-2" data-testid="saving-section">
                          <div className="flex items-center gap-2 mb-1">
                            <TrendingDown className="h-4 w-4 text-green-600" />
                            <span className="font-semibold text-green-700 dark:text-green-400">Mögliche Einsparung</span>
                          </div>
                          <p className="text-green-600">
                            <span className="font-bold">{fmt2(savingPerUnit)} EUR/{selectedSku.uom}</span> günstiger als Ihr EK-Preis ({fmt2(savingInfo.ek_price_eur)} EUR/{savingInfo.ek_price_uom || selectedSku.uom})
                          </p>
                          <p className="text-xs text-green-600/80 mt-0.5">
                            Gesamt-Saving: <span className="font-medium">{fmt2(savingPerUnit * selectedSku.quantity)} EUR</span>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Public/Tier-2 notice — nudges towards login/approval, but no longer hides price. */}
            {restricted && (
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg" data-testid="restricted-detail-banner">
                <div className="flex items-start gap-3">
                  <Lock className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  <div className="text-sm text-amber-800 dark:text-amber-300">
                    <p className="font-semibold">
                      {isPublic
                        ? t('material.publicGateTitle', 'Mehr Details nach dem Login')
                        : t('restricted.title', 'Freigabe ausstehend')}
                    </p>
                    <p className="mt-1">
                      {isPublic
                        ? t('material.publicGateHint', 'Genaue Mengen, Standortadresse und Kontaktdaten sind für registrierte Einkäufer sichtbar.')
                        : t('restricted.approvalHint', 'Ihr Konto wartet auf Freigabe.')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions — "Request this material" always visible, routes to login when needed */}
            <div className="flex gap-3">
              <Button
                className="flex-1 bg-orange-500 hover:bg-orange-600 h-12 text-base"
                onClick={handleRequestClick}
                disabled={selectedSku?.status === 'disposed' || selectedSku?.status === 'reserved'}
                data-testid="request-material-btn"
              >
                {t('material.request', 'Material anfragen')}
              </Button>
              {user && (
                <Button
                  variant="outline"
                  size="icon"
                  className={cn("h-12 w-12", isInWatchlist && "text-red-500 border-red-200")}
                  onClick={handleWatchlistToggle}
                  data-testid="watchlist-toggle-btn"
                >
                  <Heart className={cn("h-5 w-5", isInWatchlist && "fill-current")} />
                </Button>
              )}
              {canManage && (
                <>
                  {isInternal && (
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-12 w-12 text-green-600 border-green-200 hover:bg-green-50"
                      onClick={handleReleaseExternal}
                      title="Jetzt extern freigeben"
                      data-testid="release-external-btn"
                    >
                      <Globe className="h-5 w-5" />
                    </Button>
                  )}
                  {isInternal && selectedSku && (
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-12 w-12 text-amber-600 border-amber-200 hover:bg-amber-50"
                      onClick={async () => {
                        try {
                          const newDays = prompt('Internes Fenster verkürzen: Neue Tage ab Erstellungsdatum (z.B. 7)');
                          if (newDays === null) return;
                          const days = parseInt(newDays);
                          if (isNaN(days) || days < 0) { toast.error('Bitte gültige Tage eingeben'); return; }
                          await updateSkuInternalWindow(selectedSku.sku_id, { internal_window_days: days });
                          toast.success(`Internes Fenster auf ${days} Tage gesetzt`);
                          loadMaterial();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Fehler');
                        }
                      }}
                      title="Internes Fenster verkürzen"
                      data-testid="shorten-internal-window-btn"
                    >
                      <Timer className="h-5 w-5" />
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-12 w-12"
                    onClick={() => navigate(`/material/${materialId}/edit`)}
                    data-testid="edit-material-btn"
                  >
                    <Edit className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-12 w-12 text-red-500 border-red-200 hover:bg-red-50"
                    onClick={() => setDeleteDialogOpen(true)}
                    data-testid="delete-material-btn"
                  >
                    <Trash2 className="h-5 w-5" />
                  </Button>
                </>
              )}
            </div>

            {/* SKU Selector (if multiple) — available for all tiers so public users can see locations */}
            {material.skus.length > 1 && (
              <div>
                <h3 className="font-semibold mb-2">{t('material.selectLocation', 'Standort wählen')}</h3>
                <div className="space-y-2">
                  {material.skus.map((sku) => (
                    <button
                      key={sku.sku_id}
                      onClick={() => setSelectedSku(sku)}
                      className={cn(
                        "w-full p-3 rounded-lg border text-left transition-colors",
                        selectedSku?.sku_id === sku.sku_id 
                          ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20"
                          : "hover:border-slate-300"
                      )}
                      data-testid={`sku-selector-${sku.sku_id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="font-medium">{sku.site?.site_name}</span>
                          <p className="text-sm text-muted-foreground">
                            {sku.site?.city}, {sku.site?.country}
                          </p>
                        </div>
                        <span className="text-muted-foreground text-sm">{sku.quantity} {sku.uom}</span>
                      </div>
                      {/* Site/Werk rating */}
                      {sku.site_rating ? (
                        <div className="flex items-center gap-1 mt-1.5" data-testid={`sku-site-rating-${sku.sku_id}`}>
                          <div className="flex gap-px">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <Star key={s} className={cn("h-3 w-3", s <= Math.round(sku.site_rating.average) ? "fill-amber-400 text-amber-400" : "text-gray-300 dark:text-gray-600")} />
                            ))}
                          </div>
                          <span className="text-xs text-amber-600 font-medium">{sku.site_rating.average}</span>
                          <span className="text-xs text-muted-foreground">({sku.site_rating.total_ratings})</span>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground mt-1.5">{t('transactions.noReviews', 'Noch keine Bewertungen')}</p>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <Separator />

            {/* Specs */}
            <Tabs defaultValue="specs" className="w-full">
              <TabsList className={cn("grid w-full", isPublic ? "grid-cols-2" : "grid-cols-3")}>
                <TabsTrigger value="specs">Specifications</TabsTrigger>
                <TabsTrigger value="docs">Documents</TabsTrigger>
                {!isPublic && <TabsTrigger value="seller">Seller</TabsTrigger>}
              </TabsList>
              
              <TabsContent value="specs" className="space-y-3 pt-4">
                <div className="grid grid-cols-2 gap-3">
                  {material.material_width_mm && (
                    <div className="flex items-center gap-2">
                      <Ruler className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">Width: {material.material_width_mm}mm</span>
                    </div>
                  )}
                  {material.material_thickness_optional && (
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">Thickness: {material.material_thickness_optional}μm</span>
                    </div>
                  )}
                  {material.core_diameter_optional && (
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">Core: {material.core_diameter_optional}mm</span>
                    </div>
                  )}
                  {material.material_index && (
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm" data-testid="material-index-display">Material-Index: {material.material_index}</span>
                    </div>
                  )}
                  {selectedSku?.erp_article_number && (
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm" data-testid="erp-article-display">ERP: {selectedSku.erp_article_number}</span>
                    </div>
                  )}
                  {selectedSku?.production_date && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        Produced: {new Date(selectedSku.production_date).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>

                {selectedSku?.compliance_flag && (
                  <div className="flex items-center gap-2 text-green-600">
                    <Shield className="h-4 w-4" />
                    <span className="text-sm">Compliance Certified</span>
                  </div>
                )}

                {material.common_description && (
                  <div className="pt-3">
                    <p className="text-sm font-medium mb-1">Description</p>
                    <p className="text-sm text-muted-foreground">{material.common_description}</p>
                  </div>
                )}

                {selectedSku?.notes_external && (
                  <div className="pt-3">
                    <p className="text-sm font-medium mb-1">Notes</p>
                    <p className="text-sm text-muted-foreground">{selectedSku.notes_external}</p>
                  </div>
                )}

                {/* Master Data Info from Stammdaten */}
                {user && masterDataMatch && (
                  <div className="pt-3 mt-3 border-t border-slate-200 dark:border-slate-700" data-testid="master-data-info">
                    <p className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <ClipboardList className="h-4 w-4 text-orange-500" />
                      Stammdaten
                    </p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      {masterDataMatch.product_name && (
                        <>
                          <span className="text-muted-foreground">Produktname:</span>
                          <span>{masterDataMatch.product_name}</span>
                        </>
                      )}
                      {masterDataMatch.material_type && (
                        <>
                          <span className="text-muted-foreground">Materialtyp:</span>
                          <span>{masterDataMatch.material_type}</span>
                        </>
                      )}
                      {masterDataMatch.face_material && (
                        <>
                          <span className="text-muted-foreground">Obermaterial:</span>
                          <span>{masterDataMatch.face_material}</span>
                        </>
                      )}
                      {masterDataMatch.adhesive_type && (
                        <>
                          <span className="text-muted-foreground">Klebstofftyp:</span>
                          <span>{masterDataMatch.adhesive_type}</span>
                        </>
                      )}
                      {masterDataMatch.liner_type && (
                        <>
                          <span className="text-muted-foreground">Trägermaterial:</span>
                          <span>{masterDataMatch.liner_type}</span>
                        </>
                      )}
                      {masterDataMatch.total_thickness_um && (
                        <>
                          <span className="text-muted-foreground">Gesamtdicke:</span>
                          <span>{masterDataMatch.total_thickness_um} μm</span>
                        </>
                      )}
                      {masterDataMatch.basis_weight_gm2 && (
                        <>
                          <span className="text-muted-foreground">Flächengewicht:</span>
                          <span>{masterDataMatch.basis_weight_gm2} g/m²</span>
                        </>
                      )}
                      {masterDataMatch.application_temp_min != null && (
                        <>
                          <span className="text-muted-foreground">Temp.-Bereich:</span>
                          <span>{masterDataMatch.application_temp_min}°C bis {masterDataMatch.application_temp_max}°C</span>
                        </>
                      )}
                      {masterDataMatch.certifications && (
                        <>
                          <span className="text-muted-foreground">Zertifizierungen:</span>
                          <span>{masterDataMatch.certifications}</span>
                        </>
                      )}
                      {masterDataMatch.ean_code && (
                        <>
                          <span className="text-muted-foreground">EAN-Code:</span>
                          <span>{masterDataMatch.ean_code}</span>
                        </>
                      )}
                      {masterDataMatch.spec_code && (
                        <>
                          <span className="text-muted-foreground">Spec-Code:</span>
                          <span>{masterDataMatch.spec_code}</span>
                        </>
                      )}
                    </div>
                    {(masterDataMatch.fsc || masterDataMatch.food_safe) && (
                      <div className="flex gap-2 mt-2">
                        {masterDataMatch.fsc && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300" data-testid="master-fsc-badge">
                            <Leaf className="h-3 w-3" /> FSC
                          </span>
                        )}
                        {masterDataMatch.food_safe && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300" data-testid="master-food-safe-badge">
                            <UtensilsCrossed className="h-3 w-3" /> Food Safe
                          </span>
                        )}
                      </div>
                    )}
                    {masterDataMatch.notes && (
                      <p className="text-xs text-muted-foreground mt-2">{masterDataMatch.notes}</p>
                    )}
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="docs" className="pt-4">
                {/* Upload section - only for authorized users */}
                {canManage && (
                  <div className="mb-4">
                    <MaterialFileUpload materialId={materialId} onUploadComplete={loadMaterial} />
                  </div>
                )}
                <AttachmentsList
                  attachments={[
                    ...(masterDataSheet ? [{
                      attachment_id: 'master-data-sheet',
                      type: 'data_sheet',
                      file_url: masterDataSheet.url,
                      _label: masterDataSheet.filename,
                      _isMasterData: true,
                    }] : []),
                    ...documents
                  ]}
                  onDelete={loadMaterial}
                  canManage={canManage}
                />
              </TabsContent>
              
              <TabsContent value="seller" className="pt-4">
                {selectedSku?.site && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Building className="h-4 w-4" />
                        {selectedSku.site.site_name}
                      </CardTitle>
                      {/* Site rating in seller tab */}
                      {selectedSku.site_rating ? (
                        <div className="flex items-center gap-2 mt-1" data-testid="seller-tab-site-rating">
                          <div className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <Star key={s} className={cn("h-4 w-4", s <= Math.round(selectedSku.site_rating.average) ? "fill-amber-400 text-amber-400" : "text-gray-300 dark:text-gray-600")} />
                            ))}
                          </div>
                          <span className="text-sm font-medium text-amber-600">{selectedSku.site_rating.average}</span>
                          <span className="text-xs text-muted-foreground">
                            ({selectedSku.site_rating.total_ratings} {selectedSku.site_rating.total_ratings === 1 ? t('transactions.review', 'Bewertung') : t('transactions.reviews', 'Bewertungen')})
                          </span>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground mt-1">{t('transactions.noReviews', 'Noch keine Bewertungen')}</p>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                        <div className="text-sm">
                          {(selectedSku.site.street || selectedSku.site.house_number) && (
                            <p>{selectedSku.site.street} {selectedSku.site.house_number}</p>
                          )}
                          {(selectedSku.site.postal_code || selectedSku.site.city) && (
                            <p>{selectedSku.site.postal_code} {selectedSku.site.city}</p>
                          )}
                          {selectedSku.site.country && (
                            <p>{selectedSku.site.state_region && `${selectedSku.site.state_region}, `}{selectedSku.site.country}</p>
                          )}
                        </div>
                      </div>

                      {isPublic && (
                        <div className="pt-3 border-t text-sm text-muted-foreground" data-testid="seller-public-login-hint">
                          {t('material.sellerPublicHint', 'Genaue Standortadresse und Kontaktdaten werden nach dem Login angezeigt.')}
                        </div>
                      )}
                      {selectedSku.site.warehouse_contact_name && (
                        <div className="pt-3 border-t">
                          <p className="text-sm font-medium mb-2">Contact</p>
                          <p className="text-sm">{selectedSku.site.warehouse_contact_name}</p>
                          {selectedSku.site.warehouse_contact_phone && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                              <Phone className="h-3 w-3" />
                              {selectedSku.site.warehouse_contact_phone}
                            </div>
                          )}
                          {selectedSku.site.warehouse_contact_email && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                              <Mail className="h-3 w-3" />
                              {selectedSku.site.warehouse_contact_email}
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>

        <SimilarMaterialsSection items={similarMaterials} />
        <MatchingWantedAdsSection materialId={materialId} />
      </div>

      {/* Request Dialog */}
      <RequestMaterialDialog
        open={requestDialogOpen}
        onOpenChange={setRequestDialogOpen}
        sku={selectedSku}
        material={material}
      />

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-material-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('myMaterials.deleteTitle') || 'Material löschen?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('myMaterials.deleteDesc') || 'Das Material und alle zugehörigen Bestände werden unwiderruflich gelöscht.'}
              <br />
              <strong>{material?.common_title}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-material-cancel">{t('common.cancel') || 'Abbrechen'}</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-500 hover:bg-red-600"
              onClick={handleDelete}
              data-testid="delete-material-confirm"
            >
              {t('common.delete') || 'Löschen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Footer />
    </div>
  );
};
